import {
  ChangeEvent,
  FocusEvent,
  ReactNode,
  useEffect,
  useMemo,
  useState,
  type FunctionComponent,
} from 'react';
import { TextInput, TextInputProps } from '@mantine/core';
import { isEmail } from '@mantine/form';
import { presentUsPhoneNumber } from '@/lib/formatters/telephone';
import { useUncontrolled } from '@mantine/hooks';
import { isPhoneNumber } from '@/lib/validators';

export interface EmailOrPhoneInputProps extends Omit<
  TextInputProps,
  'onChange'
> {
  value?: string | undefined;
  defaultValue?: string | undefined;
  onChange?: (value: string) => void;
}

// Quick and basic regexes to differentiate if the user is likely entering an email or
// phone number in the omni-input.
const minimalEmailMatch = /^[\w+\-.]+@/;
const minimalPhoneMatch = /^\+?[\d\-( ]{3,}/;

/**
 * Normalize a phone number string to E.164 format.
 */
const normalizePhoneString = (phoneString: string) => {
  return phoneString.startsWith('+')
    ? `+${phoneString.replace(/\D/g, '')}`
    : `+1${phoneString.replace(/\D/g, '')}`;
};

/**
 * TextInput that adjusts input optimization and validation based on whether the user is
 * likely entering an email or phone number.
 *
 * Ensures that the underlying form control is maintained, so that typing is not interrupted.
 */
const EmailOrPhoneInput: FunctionComponent<EmailOrPhoneInputProps> = ({
  value,
  defaultValue,
  onChange,
  onBlur,
  error,
  ...others
}) => {
  const [_value, setValue] = useUncontrolled({
    value,
    defaultValue,
    finalValue: '',
    onChange,
  });

  const [isPhone, isEmailAddress] = useMemo(() => {
    return [minimalPhoneMatch.test(_value), minimalEmailMatch.test(_value)];
  }, [_value]);

  const [internalError, setInternalError] = useState<ReactNode | null>(null);
  const resolvedError = internalError ?? error;

  const internallyValidate = (value: string) => {
    // we use form validators but apply them only if we've sniffed that the value is of that type
    const error = minimalEmailMatch.test(value)
      ? isEmail('You must enter a valid email address')(value)
      : minimalPhoneMatch.test(value)
        ? isPhoneNumber('You must enter a valid phone number')(value)
        : null;

    setInternalError(error);
    return !!error;
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    internallyValidate(_value);
    onBlur?.(event);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInternalError(null);
    setValue(event.target.value);
  };

  useEffect(() => {
    if (isPhone) {
      setInternalError(null);
      setValue(normalizePhoneString(_value));
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps
       -- we specifically only trigger this effect when isPhone changes from
          false to true, not when the value changes, so we can perform an additional
          normalization of the phone number one time when the input mode changes; this
          handles cases where a phone number is added via autocomplete or pasting.
    */
  }, [isPhone]);

  const inputProps = {
    name: 'email-or-phone',
    ...others,
    value: _value,
    error: resolvedError,
    onChange: handleChange,
    onBlur: handleBlur,
  };

  if (isPhone) {
    return (
      <TextInput
        {...inputProps}
        type="tel"
        inputMode="tel"
        // Override display of number number to friendly presentation form.
        value={presentUsPhoneNumber(_value)}
        // Emit the number value in E.164 format
        onChange={(e) => {
          handleChange({
            ...e,
            target: {
              ...e.target,
              value: normalizePhoneString(e.target.value),
            },
          });
        }}
      />
    );
  }

  if (isEmailAddress) {
    return <TextInput {...inputProps} type="email" inputMode="email" />;
  }

  return <TextInput {...inputProps} type="text" />;
};

export default EmailOrPhoneInput;
